<template>
  <Teleport to="#header">
    <CheckoutHeader />
    <!-- move-up-by-one-px is Hack to avoid border displayed as double line below header -->
    <div class="cxo-separator move-up-by-one-px" />
  </Teleport>
  <div
    v-if="pageDataLoaded"
    class="grid-container"
  >
    <ConnectionLostOverlay
      v-if="isConnectionLost"
      @close="onConnectionLostOverlayClosed"
    />
    <TechnicalError
      v-if="hasTechnicalError"
      :error-thrown="technicalErrorThrown"
      @close="onTechnicalOverlayClosed"
    />

    <div class="grid-x">
      <LazyLoad
        v-if="hasPageError"
        @viewportRenderingFinished="viewportRenderingFinished"
      >
        <ErrorPage
          id="errorPage"
          class="cell"
        />
      </LazyLoad>
      <LazyLoad
        v-else-if="isOrderConfirmed"
        @viewportRenderingFinished="viewportRenderingFinished"
      >
        <OrderConfirmation>
          <template
            v-if="!hasPropCard"
            #CreditCardBanner
          >
            <CreditCardBanner
              source="order-confirmation"
              credit-acquisition="OrderConfirmationPromote"
              bringback-page="OrderConfirmation"
              :data-rsa="orderTotal"
            />
          </template>
          <template #ProsOCP>
            <ProsOCP />
          </template>
        </OrderConfirmation>
      </LazyLoad>
      <template v-else>
        <div class="show-for-sr invisible">
          <ChatComponent id="checkoutChat" />
        </div>
        <div class="cell large-5 large-offset-1">
          <GlobalNotifications
            id="GlobalNotifications"
            @scrollToAndFocus="scrollToAndFocus"
          />
          <GlobalUserMessages />
          <CreditCardBanner
            v-if="hasPropCard && aboveTheFoldRendered"
            :credit-new-account-discount-enabled="creditNewAccountDiscountEnabled"
            :card-activation-enhancements-enabled="cardActivationEnhancementsAndCheckoutEnabled"
            :credit-new-account-dynamic-banner="creditNewAccountDynamicBanner"
            :credit-new-account-dynamic-congrats-banners-enabled="creditNewAccountDynamicCongratsBannersEnabled"
            :selected-card-id="selectedCreditCardId"
            is-congratulatory-banner-eligible
            call-discount-api-once
            :is-n-a-d-banner-enabled="false"
            source="checkout"
            bringback-page="checkout"
            credit-acquisition="Checkout"
            :data-rsa="orderTotal"
            v-bind="newDiscountApiCheckoutEnabled ? { 'credit-banner-discount-payload': creditBannerDiscountPayload } : {}"
            :is-page-killswitch-active="newDiscountApiCheckoutEnabled"
            :is-prequal-nad-experiment-enabled="isPrequalNadExperimentEnabled"
          />
          <LazyLoad @viewportRenderingFinished="viewportRenderingFinished">
            <!-- sectionUIConfigs: {
                      sectionKey: Key of section,
                      component: VueComponent of Section,
                      id, // DOM Id of section - useful to scroll element
                      childSectionsIds  // DOM Id of child sections - useful to scroll element
                        // Passing From here to make sure that ids are not duplicated here and within component
                        // SECTION_PAYMENT_METHOD will have this value
                } -->
            <!--
             The async section components are rendered directly here.
             LazyLoadComponent requires its direct children to be the "dynamic" components
             (marked with .$$lazyLoaded$$) to correctly manage the sequential rendering.
            -->
            <component
              :is="section.component"
              v-for="section in allMainSectionsSequence"
              :key="section.sectionKey"
              v-bind="section.props"
            />
          </LazyLoad>
        </div>
        <div class="cell large-4 large-offset-1">
          <div class="cxo-sticky">
            <LazyLoad
              :sequential="!hasRightContainer"
              @viewportRenderingFinished="viewportRenderingFinishedForRightSection"
            >
              <!-- TODO: High id should be added in the DOM and it should be a <section> element -->
              <OrderSummary
                id="orderSummary"
                class="padding-top-m"
                @scrollToAndFocus="scrollToAndFocus"
              >
                <template
                  v-if="hasPropCard && creditNewAccountDynamicCongratsBannersEnabled"
                  #CreditCardStatementBanner
                >
                  <CreditCardBanner
                    :credit-new-account-discount-enabled="creditNewAccountDiscountEnabled"
                    :card-activation-enhancements-enabled="cardActivationEnhancementsAndCheckoutEnabled"
                    :credit-new-account-dynamic-banner="creditNewAccountDynamicBanner"
                    :credit-new-account-dynamic-congrats-banners-enabled="creditNewAccountDynamicCongratsBannersEnabled"
                    :selected-card-id="selectedCreditCardId"
                    source="checkout"
                    bringback-page="checkout"
                    credit-acquisition="Checkout"
                    :data-rsa="orderTotal"
                    v-bind="newDiscountApiCheckoutEnabled ? { 'credit-banner-discount-payload': creditBannerDiscountPayload } : {}"
                    :is-page-killswitch-active="newDiscountApiCheckoutEnabled"
                    :call-discount-api-once="true"
                    :is-n-a-d-banner-enabled="false"
                  />
                </template>
              </OrderSummary>
              <div
                v-if="!hasPropCard"
                class="cxo-separator margin-top-s"
              />
              <!-- TODO: High create v-if statement using KS when available  -->
              <!-- TODO: High id should be added in the DOM and it should be a section element -->
              <CreditCardBanner
                v-if="!hasPropCard"
                :credit-new-account-discount-enabled="creditNewAccountDiscountEnabled"
                :card-activation-enhancements-enabled="cardActivationEnhancementsAndCheckoutEnabled"
                :credit-new-account-dynamic-banner="creditNewAccountDynamicBanner"
                :selected-card-id="selectedCreditCardId"
                call-discount-api-once
                source="checkout"
                bringback-page="checkout"
                credit-acquisition="Checkout"
                :data-rsa="orderTotal"
                v-bind="newDiscountApiCheckoutEnabled ? { 'credit-banner-discount-payload': creditBannerDiscountPayload } : {}"
                :is-page-killswitch-active="newDiscountApiCheckoutEnabled"
                :is-prequal-nad-experiment-enabled="isPrequalNadExperimentEnabled"
              />
            </LazyLoad>
          </div>
        </div>
      </template>
    </div>
  </div>

  <Teleport to="#footer">
    <template v-if="aboveTheFoldRendered">
      <!--margin top on BB is 96px but xxxl is 80px(margin-top-xxxl) need to check with UX about the diff-->
      <!--to have 5 rem(xlarge-margin-top-xxxl) on desktop and 3 rem(margin-top-xl) for MEW -->
      <div class="cxo-separator xlarge-margin-top-xxxl margin-top-xl" />
      <CheckoutFooter />
    </template>
  </Teleport>

  <!-- Element with id as deviceId is used by utag.js to include device id. This is inturn used during PlaceOrder -->
  <!-- Backbone Reference - checkout-ui-feature/src/templates/partials/checkout/mcom/checkoutMain.hbs - Line Number 119 -->
  <input
    :id="DEVICE_ID"
    type="hidden"
  >
</template>

<script setup>
import {
  onErrorCaptured, onMounted, onUnmounted, computed, ref, inject, provide, watch,
} from 'vue';
import { useStore } from 'vuex';
import breakpoints from '@common-vue/atomic-ui/src/components/picture/breakpoints.module.scss';
import {
  // TODO: HIGH import usePageViewAnalytics here
  GET_ALL_SECTIONS_INFO, HIDE_GLITCH_OVERLAY, MODULE_NAMESPACE, SHOW_GLITCH_OVERLAY, DEVICE_ID,
  HIDE_CONNECTION_LOST_OVERLAY, SHOW_CONNECTION_LOST_OVERLAY, GET_REVIEW_INFO_AND_ACTIVE_SECTION_KEY,
  HAS_PROP_CARD, usePageViewAnalytics, useMedalliaVariables, SET_IS_ABOVE_THE_FOLD_RENDERED, SET_AFTER_STEP_UP_TARGET_ON_REFRESH,
  SET_IS_RIGHT_SIDE_RENDERED, getElementToScrollAfterStepUp, clearElementToScrollAfterStepUp,
  CHECKOUT_CREDIT_DISCOUNT_BANNER_ITEMS, CHECKOUT_CREDIT_DISCOUNT_BANNER_ORIGIN, SET_ROKT_INITIALIZED,
  SET_CREDIT_STEERING_ACTION_TO_TRIGGER,
} from '@common-vue/my-checkout-consumable';
import {
  INITIAL,
  DELIVERY_OPTIONS_NAME,
  UPS_OPTIONS_NAME,
  PICKUP_INFO_NAME,
  GIFT_OPTIONS_NAME,
  SERVICE_AND_FEES_NAME,
  LOYALLIST_NAME,
  PAYMENT_METHOD_NAME,
  GIFT_CARD_NAME,
  WALLET_BALANCE_NAME,
  BILLING_ADDRESS_NAME,
  STAR_REWARDS_CREATE_ACCOUNT_NAME,
  SCHEDULE_DELIVERY_NAME,
  SECTION_SHIPPING_ADDRESS,
  SECTION_DELIVERY_OPTIONS,
  SECTION_UPS_OPTIONS,
  SECTION_SERVICE_AND_FEES,
  SECTION_GIFT_OPTIONS,
  SECTION_PICKUP_INFO,
  SECTION_LOYALLIST,
  SECTION_PAYMENT_METHOD,
  SECTION_CC_RECONFIRM,
  SECTION_GIFT_CARD,
  SECTION_BILLING_ADDRESS,
  SECTION_STAR_REWARDS_CREATE_ACCOUNT,
  SECTION_SCHEDULE_DELIVERY,
  SECTION_GLOBAL_NOTIFICATIONS,
  SECURITY_CODE,
  CCFP,
  SECTION_WALLET_BALANCE,
} from '@common-vue/my-checkout-consumable/sectionConstants';
import asyncComponent from '@common-vue/bag-checkout-common-consumable/src/utils/asyncComponent';
import { SessionStorage } from '@common-vue/bag-checkout-common-consumable/src/utils/storageUtil';
import SectionAddressShipping from '@common-vue/my-checkout-consumable/components/SectionAddressShipping';
import GlobalUserMessages from '@common-vue/my-checkout-consumable/components/GlobalUserMessages';
import GlobalNotifications from '@common-vue/my-checkout-consumable/components/GlobalNotifications';
import useLoadUtag from '@common-vue/bag-checkout-common-consumable/src/composables/useLoadUtag';
import ConnectionLostOverlay from '@common-vue/my-checkout-consumable/components/overlays/ConnectionLostOverlay';
import ChatComponent from '@common-vue/my-checkout-consumable/components/ChatComponent';
import LightWeightOrderConfirmation from '@common-vue/my-checkout-consumable/components/LightWeightOrderConfirmation';
import LazyLoad from '@components/LazyLoadComponent';
import fireExperimentTag from '@utils/fireExperimentTag';
import AsyncDataHandler from '@utils/AsyncDataHandler';
// import { SET_INITIAL_PAGE_DATA } from '@types/actions';
import CheckoutHeader from '@components/CheckoutHeader';
import CheckoutFooter from '@components/CheckoutFooter';
import isCitiRedirect from '@utils/citiRedirect';
import getOptanonConsent, { removeConsentListener } from '@utils/roktSDKPlus/roktConsent';
import SectionHeaderMainSection from './SectionHeaderMainSection';

defineProps({
  pageDataLoaded: Boolean,
});

const emit = defineEmits(['viewMounted', 'viewRendered']);

const isLargeDevice = !!(typeof window !== 'undefined' && window.matchMedia?.(`(min-width: ${breakpoints.large})`).matches);
provide('isLargeDevice', isLargeDevice);
const stickyAppendToTarget = isLargeDevice ? '.cxo-sticky' : undefined;
provide('sticky-right-container-toggletip-target', stickyAppendToTarget);
const store = useStore();
const logger = inject('logger');
const sourceApp = 'Checkout';
const PAYMENT_CREDITCARD = 'CREDITCARD'; // TODO: Need to import it from my-checkout-consumable
const hasRightContainer = ref(isLargeDevice);
const aboveTheFoldRendered = ref(false);
const CC_RECONFIRM_ID = 'CCReconfirm';
// const GIFT_CARD_ID = 'GiftCard'; // TODO: HIGH to be added later
const storeState = store.state[MODULE_NAMESPACE];
// TODO: High - fix it after fixing the POPULATE_STORE action.
const hasPageError = computed(() => storeState.hasPageError);
const hasTechnicalError = computed(() => storeState.hasTechnicalError);
const technicalErrorThrown = computed(() => storeState.technicalErrorThrown);
const isOrderConfirmed = computed(() => storeState.isOrderConfirmed);
const isConnectionLost = computed(() => storeState.isConnectionLost);
const orderTotal = computed(() => storeState.orderTotal?.replace(/[$,]/g, ''));
const hasPropCard = computed(() => store.getters[`${MODULE_NAMESPACE}/${HAS_PROP_CARD}`]);
const rewardsCardDataLoadedHandler = new AsyncDataHandler();
const isRoktSDKPlusEnabled = computed(() => store.state.killswitches.isRoktSDKPlusEnabled);
const isRoktSDKPlusEnabledForApplePay = computed(() => storeState.roktSDKPlusEnabled);
const roktApiKey = computed(() => store.state.envProps?.roktApiKey);
const creditNewAccountDiscountEnabled = computed(() => store.state.killswitches.creditNewAccountDiscountEnabled);
const cardActivationEnhancementsEnabled = computed(() => store.state.killswitches.cardActivationEnhancementsEnabled);
const cardActivationEnhancementsCheckoutEnabled = computed(
  () => store.state.killswitches.cardActivationEnhancementsCheckoutEnabled,
);
const cardActivationEnhancementsAndCheckoutEnabled = computed(
  () => cardActivationEnhancementsEnabled.value && cardActivationEnhancementsCheckoutEnabled.value,
);
const newDiscountApiCheckoutEnabled = computed(() => store.state.killswitches.newDiscountApiCheckoutEnabled);
const creditNewAccountDynamicBanner = computed(() => store.state.killswitches.creditNewAccountDynamicBanner);
const creditNewAccountDynamicCongratsBannersEnabled = computed(() => store.state.killswitches.creditNewAccountDynamicCongratsBanners);
const freeShippingTrackerForCreditSteeringPrimary = computed(() => store.state.killswitches.freeShippingTrackerForCreditSteeringPrimary);
const freeShippingTrackerForCreditSteering = computed(() => store.state.killswitches.freeShippingTrackerForCreditSteering);
const isPrequalNadExperimentEnabled = computed(() => store.state.killswitches.isPrequalNadExperimentEnabled);
const originInfo = computed(() => store.getters[`${MODULE_NAMESPACE}/${CHECKOUT_CREDIT_DISCOUNT_BANNER_ORIGIN}`]);
const items = computed(() => store.getters[`${MODULE_NAMESPACE}/${CHECKOUT_CREDIT_DISCOUNT_BANNER_ITEMS}`]);
const subTotal = computed(() => storeState.merchandiseTotalAfterDiscount);
const selectedCreditCardId = computed(() => {
  if (storeState.paymentTypeSelected === PAYMENT_CREDITCARD && !storeState.giftCardsSatisfiesOrderTotal) {
    const creditCardID = storeState.creditCard?.creditCardID;
    return creditCardID || '';
  }
  return '';
});
const creditBannerDiscountPayload = computed(() => ({
  originInfo: originInfo.value,
  items: items.value,
  subTotal: subTotal.value,
}));
const { makeOneCCGetPublicKeyCall, canRetryGetPublicKeyCall } = store.state.killswitches || {};
// OCO-5644 used within encrypted-creditcard to do single API call based on this KS
if (typeof window !== 'undefined') {
  window.makeOneCCGetPublicKeyCall = makeOneCCGetPublicKeyCall;
  window.canRetryGetPublicKeyCall = canRetryGetPublicKeyCall;
}

watch(() => store.state[MODULE_NAMESPACE].isRewardsTrackerDataLoaded, (isRewardsTrackerDataLoadedNewValue) => {
  if (isRewardsTrackerDataLoadedNewValue) {
    rewardsCardDataLoadedHandler.resolve();
  }
}, {
  immediate: true,
});
// Async components
const ErrorPage = asyncComponent(() => import(/* webpackChunkName: "CXO-ErrorPage" */ '@common-vue/my-checkout-consumable/components/ErrorPage'), {
  mandatory: true,
  showErrorOverlay: true,
  sourceApp,
  sectionName: 'Error Page',
});

const CreditCardBanner = asyncComponent(() => import(/* webpackChunkName: "CXO-CreditCardBanner", webpackPrefetch: true */ '@common-vue/creditcard-banner-consumable'), {
  sourceApp,
  sectionName: 'Credit Card Banner',
  lazy: !(isCitiRedirect() && cardActivationEnhancementsAndCheckoutEnabled.value),
});

const ProsOCP = asyncComponent(() => import(/* webpackChunkName: "CXO-ProsOCP", webpackPrefetch: true */ '@common-vue/my-checkout-consumable/components/ProsOCP'), {
  sourceApp,
  sectionName: 'Pros OCP',
});

const OrderSummary = asyncComponent(async () => {
  const [OrderSummaryComponent] = await Promise.all([
    import(/* webpackChunkName: "CXO-OrderSummary", webpackPrefetch: true */ '@common-vue/my-checkout-consumable/components/OrderSummary'),
    rewardsCardDataLoadedHandler && rewardsCardDataLoadedHandler.hasData(),
  ]);
  return OrderSummaryComponent;
}, {
  mandatory: true,
  showErrorOverlay: true,
  sourceApp,
  sectionName: 'Order Summary',
});

const TechnicalError = asyncComponent(
  () => import(/* webpackChunkName: "CXO-TechnicalError", webpackPrefetch: true */ '@common-vue/my-checkout-consumable/components/overlays/TechnicalErrorOverlay'), {
    lazy: false,
    loadingComponent: null,
    sourceApp,
    sectionName: 'Technical Error Overlay',
  }, // not participating in the LazyLoader's sequencing logic
);

const OrderConfirmation = asyncComponent(
  () => import(/* webpackChunkName: "CXO-OrderConfirmation", webpackPrefetch: true */ '@common-vue/my-checkout-consumable/components/OrderConfirmation'), {
    mandatory: true,
    showErrorOverlay: false,
    sourceApp,
    sectionName: 'Order Confirmation',
    ComponentToLoadOnError: LightWeightOrderConfirmation,
  },
);

/**
* Each Section's vue component, DOMId , childSectionsIds, isChildSection
* a) id/DOMId: Id of component to be used in DOM - this is useful to scroll on review button click
* b) component: Vue component of a section
* c) childSectionsIds: DOMids of the child sections
*    Passing it from Parent file so that ids don't have to be duplicated here and also in child
*    Ex: Service & Fees Id is needed when user clicks on review from Order Summary for Service & Fees section
*    If Id is not passed from here, then it has to be hardcoded within Service & Fees and also in Checkout.vue
*    To avoid that, passing it from here
* d) isChildSection: Lets the app know that section is shown as child
*    This could have been added from store, but as per store, everything is a section in specific order
*    Store doesn't have to worry about where the section goes.
*    It's app's resposbility to decide where section goes. Now app has decided to place few sections within a section.
*    Those sections have isChildSection property
*    If app decides to move the position out, then all app has to do is remove isChildSection property from here
*       and update SectionPaymentMethod.vue
*     SECTION_SERVICE_AND_FEES, SECTION_GIFT_OPTIONS, SECTION_GIFT_CARD section
*/
const classOverride = 'padding-bottom-s';
const PAYMENT_METHOD_ID = 'PaymentMethod';
const SECTION_COMPONENT_ID_CHILD_IDS = {
  [SECTION_SHIPPING_ADDRESS]: {
    id: 'ShippingAddress',
    component: SectionAddressShipping, // Shipping Address will be above the fold, so importing statically
  },
  [SECTION_DELIVERY_OPTIONS]: {
    id: 'DeliveryOptions',
    component: asyncComponent(
      () => import(/* webpackChunkName: "CXO-DeliveryOptions", webpackPrefetch: true */ '@common-vue/my-checkout-consumable/components/SectionDeliveryOptions'),
      { mandatory: true, sourceApp, sectionName: DELIVERY_OPTIONS_NAME },
    ),
    classOverride: {
      withSectionsShownAsSubSection: 'cxo-separator padding-top-m',
    },
  },
  [SECTION_SERVICE_AND_FEES]: {
    id: 'ServiceAndFees', // Having id here helps to scroll element on click of review from Order Summary
    component: asyncComponent(
      () => import(/* webpackChunkName: "CXO-ServiceAndFees", webpackPrefetch: true */ '@common-vue/my-checkout-consumable/components/SectionServiceAndFees'),
      { mandatory: true, sourceApp, sectionName: SERVICE_AND_FEES_NAME },
    ),
    headingTag: 'h3',
    headingTagStyle: 'h4',
    classOverride: 'cxo-separator padding-top-s',
  },
  [SECTION_GIFT_OPTIONS]: {
    id: 'GiftOptions', // Having id here helps to scroll element on click of review from Order Summary
    component: asyncComponent(
      () => import(/* webpackChunkName: "CXO-GiftOptions", webpackPrefetch: true */ '@common-vue/my-checkout-consumable/components/SectionGiftOptions'),
      { mandatory: true, sourceApp, sectionName: GIFT_OPTIONS_NAME },
    ),
    classOverride,
  },
  [SECTION_UPS_OPTIONS]: {
    id: 'UpsOptions',
    component: asyncComponent(
      () => import(/* webpackChunkName: "CXO-UpsOptions", webpackPrefetch: true */ '@common-vue/my-checkout-consumable/components/SectionUPSOptions'),
      { mandatory: true, sourceApp, sectionName: UPS_OPTIONS_NAME },
    ),
    headingTag: 'h3',
    headingTagStyle: 'large',
  },
  [SECTION_PICKUP_INFO]: {
    id: 'PickupInfo',
    component: asyncComponent(
      () => import(/* webpackChunkName: "CXO-PickupInfo", webpackPrefetch: true */ '@common-vue/my-checkout-consumable/components/SectionPickupInfo'),
      { mandatory: true, sourceApp, sectionName: PICKUP_INFO_NAME },
    ),
  },
  [SECURITY_CODE]: {
    id: PAYMENT_METHOD_ID,
  },
  [CCFP]: {
    id: PAYMENT_METHOD_ID,
  },
  [SECTION_PAYMENT_METHOD]: {
    id: PAYMENT_METHOD_ID,
    component: asyncComponent(
      () => import(/* webpackChunkName: "CXO-PaymentMethod", webpackPrefetch: true */ '@common-vue/my-checkout-consumable/components/SectionPaymentMethod'),
      { mandatory: true, sourceApp, sectionName: PAYMENT_METHOD_NAME },
    ),
    childSectionsIds: {
      [SECTION_CC_RECONFIRM]: CC_RECONFIRM_ID, // Will be used as ID for SECTION_CC_RECONFIRM in DOM from SectionPaymentMethod.vue
    },
    classOverride: {
      withSectionsShownAsSubSection: 'cxo-separator padding-top-s',
    },
  },
  [SECTION_CC_RECONFIRM]: {
    id: CC_RECONFIRM_ID, // Having id here helps to scroll element on click of review from Order Summary
    secondaryIdToScrollTo: PAYMENT_METHOD_ID,
    isChildSection: true,
  },
  [SECTION_WALLET_BALANCE]: {
    id: 'WalletBalance',
    component: asyncComponent(
      () => import(/* webpackChunkName: "CXO-WalletBalance", webpackPrefetch: true */ '@common-vue/my-checkout-consumable/components/SectionWalletBalance'),
      { mandatory: true, sourceApp, sectionName: WALLET_BALANCE_NAME },
    ),
    classOverride: {
      withSectionsShownAsSubSection: '',
      withoutSectionsShownAsSubSection: '',
    },
  },
  [SECTION_GIFT_CARD]: {
    id: 'GiftCard', // Having id here helps to scroll element on click of review from Order Summary
    component: asyncComponent(
      () => import(/* webpackChunkName: "CXO-GiftCard", webpackPrefetch: true */ '@common-vue/my-checkout-consumable/components/SectionGiftCard'),
      { mandatory: true, sourceApp, sectionName: GIFT_CARD_NAME },
    ),
    classOverride: {
      withSectionsShownAsSubSection: '',
      withoutSectionsShownAsSubSection: '',
    },
  },
  [SECTION_BILLING_ADDRESS]: {
    id: 'BillingAddress', // Having id here helps to scroll element on click of review from Order Summary
    component: asyncComponent(
      () => import(/* webpackChunkName: "CXO-BillingAddress", webpackPrefetch: true */ '@common-vue/my-checkout-consumable/components/SectionAddressBilling'),
      { mandatory: true, sourceApp, sectionName: BILLING_ADDRESS_NAME },
    ),
    classOverride,
  },
  [SECTION_SCHEDULE_DELIVERY]: {
    id: 'ScheduleDelivery',
    component: asyncComponent(
      () => import(/* webpackChunkName: "CXO-ScheduleDelivery", webpackPrefetch: true */ '@common-vue/my-checkout-consumable/components/SectionScheduleDelivery'),
      { mandatory: true, sourceApp, sectionName: SCHEDULE_DELIVERY_NAME },
    ),
  },
  [SECTION_GLOBAL_NOTIFICATIONS]: {
    id: 'GlobalNotifications',
  },
  // NOTE: Sections within sections like Service & Fees, Gift Options won't use same style class as main sections
};

if (process.env.ENTRY_POINT === 'bcom') {
  SECTION_COMPONENT_ID_CHILD_IDS[SECTION_LOYALLIST] = {
    id: 'Loyallist',
    component: asyncComponent(
      () => import(/* webpackChunkName: "CXO-Loyallist", webpackPrefetch: true */ '@common-vue/my-checkout-consumable/components/SectionLoyallist'),
      { mandatory: true, sourceApp, sectionName: LOYALLIST_NAME },
    ),
    classOverride: 'padding-top-xs cxo-separator padding-bottom-xs',
  };
}

if (process.env.ENTRY_POINT === 'mcom') {
  SECTION_COMPONENT_ID_CHILD_IDS[SECTION_STAR_REWARDS_CREATE_ACCOUNT] = {
    id: 'StarRewardsCreateAccount',
    component: asyncComponent(
      () => import(/* webpackChunkName: "CXO-StarRewardsCreateAccount", webpackPrefetch: true */ '@common-vue/my-checkout-consumable/components/SectionStarRewardsCreateAccount'),
      { mandatory: true, sourceApp, sectionName: STAR_REWARDS_CREATE_ACCOUNT_NAME },
    ),
  };
}

/**
* This will be computed when allSectionsSequenceMap is set in store
* and also when GET_ALL_SECTIONS_INFO returns different result
*/
const allMainSectionsSequence = computed(() => {
  const { allSectionsSequenceMap: allSectionsSequence, sectionsShownAsSubSection } = store.state[MODULE_NAMESPACE];
  const sectionInfo = store.getters[`${MODULE_NAMESPACE}/${GET_ALL_SECTIONS_INFO}`];
  const activeSectionKey = store.getters[`${MODULE_NAMESPACE}/${GET_REVIEW_INFO_AND_ACTIVE_SECTION_KEY}`]?.activeSectionKey;
  const allMainSections = [];
  if (allSectionsSequence && sectionInfo) {
    let firstSectionShown;
    for (const [sectionKey, { optional }] of allSectionsSequence) { // eslint-disable-line no-restricted-syntax
      const state = sectionInfo[sectionKey]?.state;
      const {
        component, id, isChildSection, childSectionsIds, classOverride: classOverrideValue,
        headingTag, headingTagStyle,
      } = SECTION_COMPONENT_ID_CHILD_IDS[sectionKey] || {};

      let finalClassOverride;
      const sectionsShownAsSubConfig = sectionsShownAsSubSection?.get(sectionKey);
      const isObjectClassOverride = classOverrideValue && typeof classOverrideValue === 'object';
      const isStringClassOverride = classOverrideValue && typeof classOverrideValue === 'string';

      if (isObjectClassOverride && sectionsShownAsSubConfig) {
        const hasAnyVisibleSubSection = sectionsShownAsSubConfig.some(
          (subSectionKey) => sectionInfo[subSectionKey]?.state,
        );
        finalClassOverride = hasAnyVisibleSubSection
          ? classOverrideValue.withSectionsShownAsSubSection
          : classOverrideValue.withoutSectionsShownAsSubSection;
      } else if (isStringClassOverride) {
        finalClassOverride = classOverrideValue;
      }
      // If Section is shown (If state is available, section is shown)
      /**
       * TODO: HIGH margin above SECTION_SERVICE_AND_FEES , SECTION_GIFT_OPTIONS, SECTION_GIFT_CARD, SECTION_BILLING_ADDRESS
            are not same as other sections. Take care of this.
       * TODO: HIGH Even border on top of these sections won't be present in all cases (unlike other sections).
          Have to take care of those scenarios
       * NOTE: If Payment is onlt present without Gift Card and Billing Address , then Padding below Payment should be  padding-bottom-m
           but if Gift Card or Billing Address is present, Gap between Payment and Gift Card will be padding-bottom-s but
           space after Billing Address will be padding-bottom-m (matching no Gift card / no billing Addres scenario)
       * As per code Right now all sections follow this 'padding-top-m padding-bottom-m cxo-separator` (Refer CheckoutSection.vue)
       */
      if (state && !isChildSection && component) { // !SECTION_SERVICE_AND_FEES && !SECTION_GIFT_OPTIONS && !SECTION_GIFT_CARD
        let isFirstSection;
        if (!firstSectionShown) {
          firstSectionShown = true;
          isFirstSection = true;
        }
        const isInInitialState = state === INITIAL && sectionKey !== activeSectionKey;
        const useOnlySectionHeader = isInInitialState && !optional;
        // If section is in INITIAL state, load only SectionHeader and not the actual component
        // Optional Sections like Gift Options/ Gift Card and Loyallist show Checkbox when state is INITIAL
        const resolvedComponent = useOnlySectionHeader ? SectionHeaderMainSection : component;
        if (resolvedComponent) {
          let finalClass;
          if (finalClassOverride === undefined) {
            const paddingTop = isFirstSection ? 'padding-top-m' : 'padding-top-s';
            finalClass = `cxo-separator ${paddingTop}`;
          } else {
            finalClass = finalClassOverride;
          }
          finalClass = `padding-bottom-s ${finalClass}`.trim();
          if (isFirstSection) {
            // Remove the separator class and leading or trailing spaces for the first section
            finalClass = finalClass.replace(/\s*cxo-separator\s*/, ' ').trim();
          }
          allMainSections.push({
            sectionKey,
            component: resolvedComponent,
            props: {
              id,
              class: finalClass,
              tabindex: -1,
              'section-key': sectionKey,
              'child-sections-ids': useOnlySectionHeader ? null : childSectionsIds,
              'heading-tag': headingTag,
              'heading-tag-style': headingTagStyle,
            },
          });
        }
      }
    }
  }
  return allMainSections;
});

const loagUtag = useLoadUtag();
const { firePageViewTag, firePageViewTagOCP } = usePageViewAnalytics();
const { setMedalliaVariables, setMedalliaRegistryVariables } = useMedalliaVariables();

/**
  * polaris/checkout-ui-feature/src/CheckoutViews/common/CheckoutApplication.js
  * - initialize function, line number 66 and 67
*/
setMedalliaVariables();

const fireViewTagAndExperimentTag = async () => {
  /**
   * Utag's 'view' tag and EXPSDK's fireAdobeTags should happen in sequence
   * Hence we are awaiting for utag library to load and then firing both tags
   */
  await loagUtag();

  const { metaProps } = store.state[MODULE_NAMESPACE];
  const { isApplePayOrderConfirmation } = metaProps || {};
  // If request is for Apple Pay Order Confirmation - Fire OCP Tag from here
  if (isApplePayOrderConfirmation) {
    setMedalliaRegistryVariables();
    // Fo Apple Pay - /my/checkout will directly load the OCP page
    // While OCP page is loaded directly , utag needs to be loaded before firing fireTag('view')
    // Hence code to fire OCP page view tag for Apple pay is here
    firePageViewTagOCP({ immediate: true });
  } else {
    // Else fire Page Tag
    firePageViewTag({ immediate: true });
  }
  // Fire 'Experimentation' tag
  fireExperimentTag();
};

const viewportRenderingFinished = async () => {
  const { metaProps } = store.state[MODULE_NAMESPACE];
  const { isApplePayOrderConfirmation } = metaProps || {};

  if (!aboveTheFoldRendered.value) {
    aboveTheFoldRendered.value = true;
    emit('viewRendered');
    store.commit(`${MODULE_NAMESPACE}/${SET_IS_ABOVE_THE_FOLD_RENDERED}`, aboveTheFoldRendered.value);
  }

  if (((isRoktSDKPlusEnabled.value && !isApplePayOrderConfirmation)
  || (isRoktSDKPlusEnabledForApplePay.value && isApplePayOrderConfirmation)) && !window.mParticle?.Rokt) {
    setTimeout(async () => {
      try {
        const roktConsentEnabled = await getOptanonConsent();
        if (roktConsentEnabled) {
          const { default: loadRoktSDKPlusScript } = await import(/* webpackPrefetch: true */ '@utils/roktSDKPlus/loadRoktSDKPlusScript');
          await loadRoktSDKPlusScript({ brand: process.env.ENTRY_POINT, apiKey: roktApiKey.value, screenName: 'checkout' });
          store.commit(`${MODULE_NAMESPACE}/${SET_ROKT_INITIALIZED}`, true);
        }
      } catch (e) {
        store.commit(`${MODULE_NAMESPACE}/${SET_ROKT_INITIALIZED}`, false);
        logger.error('ROKT - error loading sdk plus script - ', e);
      }
    }, 0);
  }
};

watch(isOrderConfirmed, async (isOrderConfirmedNewValue, isOrderConfirmedOldValue) => {
  if (isRoktSDKPlusEnabled.value && !isOrderConfirmedOldValue && isOrderConfirmedNewValue === true) {
    try {
      const { trackRoktPageView } = await import('@utils/roktSDKPlus/loadRoktSDKPlusScript');
      trackRoktPageView('confirmation');
    } catch (e) {
      logger.error('ROKT - error tracking order confirmation page view - ', e);
    }
  }
});

// Listening this on Right side's view port rendering finished,
function viewportRenderingFinishedForRightSection() {
  store.commit(`${MODULE_NAMESPACE}/${SET_IS_RIGHT_SIDE_RENDERED}`, true);
}
/**
* This is used when user clicks on Review from OrderSummary Section
* @param keyOfContentToScrollTo  - Symbol - Key of Content to which user has to be scrolled to
* @param needsFocus - Boolean - passed to retain the focus
*/
function scrollToAndFocus({ keyOfContentToScrollTo, needsFocus = true } = {}) {
  const { id, secondaryIdToScrollTo } = SECTION_COMPONENT_ID_CHILD_IDS[keyOfContentToScrollTo] || {};

  let element = id && document.getElementById(id);
  // Section Credit Card Rekey section is within payment, it will not load unless user has scrolled down to payment
  // 1. If user clicks on review for Credit card number rekey on page load and if payment section is not visible,
  //   then scrolling the user to Payment so that user can see credit card number rekey section
  // 2. When Credit Card Rekey is already loaded and after that if user clicks on reivew, its better to scroll
  //  to Credit card Rekey section (ideally this is expected), hence having both id and secondaryIdToScrollTo
  if (!element && secondaryIdToScrollTo) {
    element = document.getElementById(secondaryIdToScrollTo);
  }

  if (element && element.scrollIntoView) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
  if (element && needsFocus && element.focus) {
    element.focus({
      preventScroll: true,
    });
  }
}

const onReady = (/* { isDataRevalidated } = {} */) => {
  fireViewTagAndExperimentTag();

  // This is needed for Step Up Auth Scenario
  // In Vue Component is laoded lazily so its required to scroll the user to section to load the component
  setTimeout(() => {
    const { keyOfSectionToScrollTo, stepUpOriginatedActionIdentifier } = getElementToScrollAfterStepUp() || {};
    if (keyOfSectionToScrollTo) {
      scrollToAndFocus({ keyOfContentToScrollTo: keyOfSectionToScrollTo });
      // if user clicked on Edit From Payment/Shipping or Add new from payment or shipping
      // and if step up is needed, then page refreshes,
      // In this scenario, capture the element which triggered click
      store.commit(`${MODULE_NAMESPACE}/${SET_AFTER_STEP_UP_TARGET_ON_REFRESH}`, {
        keyOfSectionToScrollTo,
        stepUpOriginatedActionIdentifier,
      });
      clearElementToScrollAfterStepUp();
    }

    try {
      const pendingCardId = SessionStorage.getItem('creditSteeringActionToTrigger');
      if (pendingCardId && (freeShippingTrackerForCreditSteering.value || freeShippingTrackerForCreditSteeringPrimary.value)) {
        store.commit(`${MODULE_NAMESPACE}/${SET_CREDIT_STEERING_ACTION_TO_TRIGGER}`, pendingCardId);
        SessionStorage.removeItem('creditSteeringActionToTrigger');
        scrollToAndFocus({ keyOfContentToScrollTo: SECTION_PAYMENT_METHOD, needsFocus: false });
      }
    } catch (error) {
      logger.error('Error while reading credit steering action from session storage', error);
    }
  });
};

function openTechnicalErrorOverlay(error) {
  store.commit(`${MODULE_NAMESPACE}/${SHOW_GLITCH_OVERLAY}`, error);
}

function onTechnicalOverlayClosed() {
  store.commit(`${MODULE_NAMESPACE}/${HIDE_GLITCH_OVERLAY}`);
}

function openConnectionLostOverlay() {
  store.commit(`${MODULE_NAMESPACE}/${SHOW_CONNECTION_LOST_OVERLAY}`);
}

function onConnectionLostOverlayClosed() {
  store.commit(`${MODULE_NAMESPACE}/${HIDE_CONNECTION_LOST_OVERLAY}`);
}

onErrorCaptured((/* error, instance, info */ error) => {
  // Open Technical error overlay to inform user about error
  if (!navigator.onLine) {
    logger.error('User is offline, hence an error is thrown', error);
    openConnectionLostOverlay();
  } else {
    openTechnicalErrorOverlay(error);
  }
  // Let the error propagate further
  return true;
});

onMounted(() => {
  emit('viewMounted');
});

onUnmounted(() => {
  removeConsentListener();
});

defineExpose({ // eslint-disable-line no-undef
  onReady,
});
</script>

<style src="appSCSS" lang='scss'></style>
<style src="./css/global.scss" lang="scss" />
<style src="./css/common.scss" lang="scss" scoped />
